import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaWhatsapp } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import { businessLogic } from '../../lib/constants';
import { trackConversion } from '../../lib/analytics';

/**
 * Botón flotante de WhatsApp con burbuja de invitación.
 *
 * Sustituye al botón que vivía en el appbar, así que se monta desde el mismo
 * header y aparece en las mismas páginas que antes.
 *
 * Dos detalles que no son evidentes:
 *  - La home lleva una barra de cookies fija abajo a todo el ancho: el FAB se
 *    levanta por encima de ella mientras esté visible, y vuelve a su sitio al
 *    aceptarla.
 *  - La burbuja NO se auto-abre dentro de las áreas privadas (portal y
 *    afiliados): ahí el mensaje comercial no pinta nada. El botón sí sigue,
 *    porque ya estaba antes.
 */

const BUBBLE_DISMISSED_KEY = 'wa_bubble_dismissed';
const BUBBLE_DELAY_MS = 3500;
// /demo: la burbuja tapaba la esquina de la app embebida (la demo en vivo).
const PRIVATE_PATHS = ['/customer-portal', '/affiliates/dashboard', '/demo'];

const isPrivateArea = () => {
  try {
    return PRIVATE_PATHS.some((p) => window.location.pathname.startsWith(p));
  } catch (e) {
    return false;
  }
};

const wasDismissed = () => {
  try {
    return localStorage.getItem(BUBBLE_DISMISSED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const WhatsAppFab = () => {
  const { t } = useTranslation();
  const [showBubble, setShowBubble] = useState(false);
  const [cookieBarHeight, setCookieBarHeight] = useState(0);

  // La burbuja entra sola una vez, pasado un momento: si sale de golpe al
  // cargar, tapa el hero y se percibe como intrusiva.
  useEffect(() => {
    if (wasDismissed() || isPrivateArea()) return undefined;
    const timer = setTimeout(() => setShowBubble(true), BUBBLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // La barra de cookies es fixed abajo y a todo el ancho: sin esto, el FAB se
  // le monta encima.
  //
  // No basta con medirla una vez: la primera medida cae ANTES de que la barra
  // tenga su alto final (el texto reflowa al cargar la fuente) y se quedaba
  // corta -- medido 20px cuando acaba midiendo 77. Por eso hay un
  // ResizeObserver sobre la barra ademas del MutationObserver que detecta
  // cuando aparece o se acepta.
  useEffect(() => {
    let barObserver = null;
    let current = null;

    const measure = () => {
      const bar = document.querySelector('.CookieConsent');
      setCookieBarHeight(bar ? bar.getBoundingClientRect().height : 0);

      // Reengancha el observador si la barra ha entrado o cambiado.
      if (bar !== current) {
        current = bar;
        if (barObserver) barObserver.disconnect();
        if (bar && typeof ResizeObserver !== 'undefined') {
          barObserver = new ResizeObserver(() => {
            setCookieBarHeight(bar.getBoundingClientRect().height);
          });
          barObserver.observe(bar);
        }
      }
    };

    measure();
    const domObserver = new MutationObserver(measure);
    domObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);
    // Tras cargar fuentes el texto de la barra reflowa y cambia su alto.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }

    return () => {
      domObserver.disconnect();
      if (barObserver) barObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const dismiss = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowBubble(false);
    try {
      localStorage.setItem(BUBBLE_DISMISSED_KEY, '1');
    } catch (err) {
      /* sin storage la burbuja solo se cierra en esta visita */
    }
  }, []);

  const offset = cookieBarHeight ? cookieBarHeight + 16 : 0;

  return (
    <div
      className="wa-fab"
      style={offset ? { '--wa-fab-offset': `${offset}px` } : undefined}
      onMouseEnter={() => !isPrivateArea() && setShowBubble(true)}
    >
      {showBubble ? (
        <div className="wa-fab__bubble" role="status">
          <button
            type="button"
            className="wa-fab__close"
            onClick={dismiss}
            aria-label={t('wa_fab_close')}
          >
            <FiX size={13} />
          </button>
          <a
            href={businessLogic.whatsappLink}
            target="_blank"
            rel="noreferrer"
            className="wa-fab__bubble-link"
            onClick={() => trackConversion('WhatsApp FAB Bubble')}
          >
            <strong>{t('wa_fab_title')}</strong>
            <span>{t('wa_fab_text')}</span>
          </a>
        </div>
      ) : null}

      <a
        href={businessLogic.whatsappLink}
        target="_blank"
        rel="noreferrer"
        className="wa-fab__button"
        onClick={() => trackConversion('WhatsApp FAB')}
        aria-label={t('wa_fab_aria')}
        title={t('wa_fab_aria')}
      >
        <FaWhatsapp size={30} />
      </a>
    </div>
  );
};

export default WhatsAppFab;
